/**
 * A custom element that counts down to a fixed UTC date/time.
 *
 * The `datetime` attribute must be an ISO-8601 date/time. If it has no timezone
 * designator (no trailing `Z` or `+HH:MM`/`-HH:MM` offset), it's treated as UTC —
 * merchants are asked to enter a UTC value, but browsers parse a bare
 * `YYYY-MM-DDTHH:MM:SS` string as *local* time, so this normalizes it to avoid
 * silently shifting the countdown by the visitor's UTC offset.
 *
 * Since both the target date and `Date.now()` are absolute instants, the remaining
 * duration is correct for every visitor regardless of their local timezone.
 */
class CountdownTimer extends HTMLElement {
  /** @type {number|undefined} */
  #interval;

  /** @type {number} */
  #target;

  connectedCallback() {
    const raw = (this.getAttribute('datetime') ?? '').trim();
    const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw);

    this.#target = Date.parse(hasTimezone ? raw : `${raw}Z`);

    if (Number.isNaN(this.#target)) return;

    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  disconnectedCallback() {
    window.clearInterval(this.#interval);
  }

  #tick() {
    const diff = this.#target - Date.now();

    if (diff <= 0) {
      window.clearInterval(this.#interval);
      this.#setValue('days', 0);
      this.#setValue('hours', 0);
      this.#setValue('minutes', 0);
      this.#setValue('seconds', 0);
      this.#toggleUnit('days', false);
      this.toggleAttribute('complete', true);

      if (this.hasAttribute('hide-on-complete')) {
        this.hidden = true;
      }

      return;
    }

    const totalSeconds = Math.floor(diff / 1000);
    const days = Math.floor(totalSeconds / 86400);

    this.#setValue('days', days);
    this.#setValue('hours', Math.floor((totalSeconds % 86400) / 3600));
    this.#setValue('minutes', Math.floor((totalSeconds % 3600) / 60));
    this.#setValue('seconds', totalSeconds % 60);
    // this.#toggleUnit('days', days > 0);
  }

  /**
   * @param {'days'|'hours'|'minutes'|'seconds'} unit
   * @param {number} value
   */
  #setValue(unit, value) {
    const target = this.querySelector(`[data-${unit}]`);
    if (target) target.textContent = String(value).padStart(2, '0');
  }

  /**
   * @param {'days'|'hours'|'minutes'|'seconds'} unit
   * @param {boolean} visible
   */
  #toggleUnit(unit, visible) {
    const value = this.querySelector(`[data-${unit}]`);
    const unitElement = value?.closest('.countdown__unit');
    if (unitElement instanceof HTMLElement) unitElement.classList.add("hidden");
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
